import forge from "node-forge";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Button from "../../components/FormComponents/Button";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import Input from "../../components/FormComponents/Input";
import {
  BASE_URL,
  ENCRYPTION_PUBLIC_KEY,
  SAML_LOGIN_URL,
  SHOW_COGNITO_LOGIN,
  SHOW_DEFAULT_LOGIN,
  SHOW_SAML_LOGIN,
  STORAGE_KEYS,
  USER_SERVICE_ENCRYPTION_PUBLIC_KEY
} from "../../constant/constant";
import { useAuth0Context } from "../../context/Auth0Context";
import { useAxiosContext } from "../../context/AxiosContext";
import loginDown from "../../images/svg/login-mg-down.svg";
import loginImg from "../../images/svg/loginImg.svg";
import waveBg from "../../images/svg/loginWavetop.svg";
import zinoLogo from "../../images/svg/zinoLogo.svg";
import storageService from "../../services/storageService";
import { toastError } from "../../utils/toastUtils";
import "./Login.css";
import { ROOT_ROUTE_SEGMENTS } from "../../routes/routesConstant";

const Login = () => {
  const [loginCredential, setLoginCredential] = useState({
    email: "",
    password: ""
  });
  const { axiosInstance, handleError } = useAxiosContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser, setIsAuthenticate, isAuthenticate } = useAuth0Context();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState("");
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const SHOW_ANY_SSO = SHOW_COGNITO_LOGIN || SHOW_SAML_LOGIN;

  //external login service token
  const code = searchParams.get("code");
  const service = searchParams.get("state");

  const validateLogin = () => {
    const newErrors = {};
    const trimmedEmail = loginCredential.email.trim();
    if (!trimmedEmail) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!loginCredential.password) {
      newErrors.password = "Password is required";
    }
    return newErrors;
  };

  const handleLoginData = (e) => {
    setLoginCredential({
      ...loginCredential,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const validationErrors = validateLogin();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoginError("");
    setLoading(true);

    const newLoginCredentialsWithEncryptedPassword = {
      ...loginCredential,
      email: encryptData(loginCredential.email),
      password: encryptData(loginCredential.password)
    };

    try {
      const { data } = await axiosInstance.post("/user/login", newLoginCredentialsWithEncryptedPassword);
      handleTokenVarification(data);
    } catch (error) {
      console.log({ error });
      setLoginError(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCognitoLogin = async () => {
    try {
      const { data } = await axiosInstance.get("/user-services/login/cognito");
      window.location.href = data.url;
    } catch (error) {
      handleError(error);
    }
  };

  const handleSSOLogin = async () => {
    window.location.href = `${BASE_URL}/${SAML_LOGIN_URL}`;
  };

  const handleTokenVarification = (data) => {
    const decode = JSON.parse(atob(data.split(".")[1]));
    setUser(decode);
    setToken(data);
    setIsAuthenticate(true);
    storageService.set(STORAGE_KEYS.ZINO_TOKEN, data);

    const params = new URLSearchParams(location.search);
    const redirectPath = params.get("redirect");

    const roles = decode?.role_names || [];

    const hasUserAdmin = roles.includes("user-admin");
    const hasEditor = roles.includes("editor");
    const isOnlyUserAdmin = roles.length === 1 && hasUserAdmin;
    const isOnlyUserAdminAndEditor = roles.length === 2 && hasUserAdmin && hasEditor;

    if (isOnlyUserAdmin || isOnlyUserAdminAndEditor) {
      navigate(`/${ROOT_ROUTE_SEGMENTS.USER_MANAGEMENT}`);
    }

    navigate(redirectPath || `/${ROOT_ROUTE_SEGMENTS.ORGANIZATION}`);
  };

  const verifyCognitoLoginToken = async (service, code) => {
    try {
      const { data } = await axiosInstance.get(`/user-services/verify/cognito?code=${code}`);
      handleTokenVarification(data);
    } catch (error) {
      toastError(error?.response?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (isAuthenticate) {
      navigate("/organization");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticate]);

  useEffect(() => {
    if (code && service) {
      verifyCognitoLoginToken(service, code);
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, service]);

  return (
    <div className="login_outer_container">
      <div className="login-page-img-wrap">
        <img className="login-img" src={loginImg} alt="login Img" />
        <img className="login-wave-bg" src={waveBg} alt="wave bg" />
        <div className="login-text">
          Build, Innovate, and Automate <br></br> <span>AI-Powered Low-Code</span> Platform for Businesses.
          {/* applications with <span>Gen-AI</span> based <br></br> <span>LOW-CODE</span> */}
        </div>
        <img className="login-img-down" src={loginDown} alt="login bg down" />
      </div>

      <div className="login-right-section">
        <div className="logo-in-login">
          <img src={zinoLogo} alt="zino logo" />
        </div>
        <div className="login_inner_container ">
          <div style={{ padding: "20px 0px" }}>
            {SHOW_DEFAULT_LOGIN && (
              <>
                <form className="builder_form login-form" onSubmit={handleLogin}>
                  <Input
                    label="Email"
                    type="text"
                    value={loginCredential.email}
                    name="email"
                    onChange={handleLoginData}
                    placeholder="Enter Email ID"
                    error={errors.email}
                  />

                  <div>
                    <div className="position_relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        label="Password"
                        value={loginCredential.password}
                        name="password"
                        onChange={handleLoginData}
                        placeholder="Enter Password"
                        error={errors.password}
                      />
                      <MaterialIcon
                        name={showPassword ? "visibility_off" : "visibility"}
                        className="password_visible cursor_pointer"
                        onClick={togglePasswordVisibility}
                      />
                    </div>
                    <div className="forgot_password mt_2" onClick={() => navigate("../resetpassword")}>
                      Forgot Password?
                    </div>
                  </div>
                  <div className="error-message-container">
                    {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
                  </div>
                  <Button type="submit" style={{ maxWidth: "unset" }} disabled={loading}>
                    {loading ? <span className="login_spinner" /> : "Login"}
                  </Button>
                </form>
                {SHOW_ANY_SSO && (
                  <div className="text_center my_3" style={{ color: "#10182b" }}>
                    or
                  </div>
                )}
              </>
            )}
            <div className="d_flex flex_column gap_2">
              {SHOW_COGNITO_LOGIN && (
                <Button style={{ maxWidth: "none", width: "100%" }} variant="secondary" onClick={handleCognitoLogin}>
                  Login With Cognito
                </Button>
              )}
              {SHOW_SAML_LOGIN && (
                <Button style={{ maxWidth: "none", width: "100%" }} variant="secondary" onClick={handleSSOLogin}>
                  Continue With SSO
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const publicKeyPem = ENCRYPTION_PUBLIC_KEY?.replace(/\\n/g, "\n");
const publicKeyPemForUserService = USER_SERVICE_ENCRYPTION_PUBLIC_KEY?.replace(/\\n/g, "\n");
export const encryptData = (data, isUserService = false) => {
  const publicKey = isUserService ? publicKeyPemForUserService : publicKeyPem;
  try {
    const forgedPublicKey = forge.pki.publicKeyFromPem(publicKey);
    const encrypted = forgedPublicKey.encrypt(forge.util.encodeUtf8(data), "RSAES-PKCS1-V1_5");
    return forge.util.encode64(encrypted);
  } catch (error) {
    console.log({ error });
  }
};

export default Login;
